import { useId } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/utils/cn';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

interface PaginationProps {
  page: number;
  pageSize: number;
  totalRecords: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  className?: string;
}

export function Pagination({
  page,
  pageSize,
  totalRecords,
  onPageChange,
  onPageSizeChange,
  className,
}: PaginationProps) {
  const pageSizeId = useId();
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

  if (totalRecords === 0) return null;

  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalRecords);

  return (
    <nav
      className={cn(
        'flex items-center justify-between border-t border-cool-gray-2 pt-3',
        className,
      )}
      aria-label="Pagination"
    >
      {/* Page numbers — left side */}
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="inline-flex items-center justify-center h-8 w-8 border border-cool-gray-2 text-cool-gray-10 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          aria-label="Previous page"
        >
          <ChevronLeft size={16} />
        </button>
        {Array.from({ length: totalPages }, (_, i) => i + 1)
          .filter((p) => {
            if (p === 1 || p === totalPages) return true;
            if (Math.abs(p - page) <= 1) return true;
            return false;
          })
          .reduce<(number | '...')[]>((acc, p, idx, arr) => {
            if (idx > 0) {
              const prev = arr[idx - 1]!;
              if (p - prev > 1) acc.push('...');
            }
            acc.push(p);
            return acc;
          }, [])
          .map((item, idx) =>
            item === '...' ? (
              <span key={`ellipsis-${idx}`} className="px-1 text-sm text-cool-gray-10">
                &hellip;
              </span>
            ) : (
              <button
                key={item}
                type="button"
                onClick={() => onPageChange(item)}
                className={cn(
                  'inline-flex items-center justify-center h-8 min-w-[2rem] px-2 text-sm border',
                  item === page
                    ? 'border-black bg-black text-white'
                    : 'border-cool-gray-2 text-cool-gray-10 hover:bg-gray-50',
                )}
                aria-current={item === page ? 'page' : undefined}
                aria-label={`Page ${item}`}
              >
                {item}
              </button>
            ),
          )}
        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          className="inline-flex items-center justify-center h-8 w-8 border border-cool-gray-2 text-cool-gray-10 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          aria-label="Next page"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Page size selector + summary — right side */}
      <div className="flex items-center gap-4">
        {onPageSizeChange && (
          <div className="flex items-center gap-2">
            <label htmlFor={pageSizeId} className="text-sm text-cool-gray-10 whitespace-nowrap">
              Rows per page
            </label>
            <select
              id={pageSizeId}
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="w-16 border border-cool-gray-2 py-1 pl-2 pr-7 text-sm text-black appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%2363666a%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:16px] bg-[right_4px_center] bg-no-repeat focus:border-black focus:outline-none focus:ring-0"
              aria-label="Rows per page"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        )}

        <p className="text-sm text-cool-gray-10">
          Showing <span className="font-semibold text-black">{start}</span> to{' '}
          <span className="font-semibold text-black">{end}</span> of{' '}
          <span className="font-semibold text-black">{totalRecords}</span> results
        </p>
      </div>
    </nav>
  );
}
